import {
    AfterViewInit,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    Renderer2,
    SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { AumicoStyleOptions } from '@modeso/types__aumico-lib-core-be';
import { AumFinancialNumberPipe, NumberUtils } from '../../../../../aumico-lib-core-fe/src/lib';

@Directive({
    selector: '[hamInput]',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: HamInputDirective,
            multi: true,
        },
        AumFinancialNumberPipe,
    ],
})
export class HamInputDirective implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor, OnChanges {
    @Input() label: string | null = null;
    @Input() icon: string | null = null;
    @Input() suffixIcon: string | null = null;
    @Input() prefixIcon: string | null = null;
    @Input() iconColor: string | null = null;
    @Input() aumicoStyleOptions: AumicoStyleOptions | AumicoStyleOptions[] | undefined;
    @Input() decimalPlaces: number = 2;
    @Input() showRequired: boolean = false; // add indication for required field
    @Input() hideRemoveXButton: boolean = false; // add indication for required field
    @Input() invalidRef: boolean = false; // show #REF! in red, overrides readonly and number type
    @Input() shouldHighliteBlueWhenValueEntered: boolean = false; // add blue highlight when value is entered always (standard behaviour is to only show blue border if element is focused)
    /** When true: teal styling + readonly + pen icon. Clicking the pen unlocks the field. */
    @Input() zefixLocked: boolean = false;
    /** When true: orange/yellow styling indicating the field was originally from Zefix but manually overridden. */
    @Input() set zefixOverridden(value: boolean) {
        this._zefixOverridden = value;
        if (this.inputWrapperElement) {
            if (value) {
                this.renderer.addClass(this.inputWrapperElement, 'zefix-overridden');
            } else {
                this.renderer.removeClass(this.inputWrapperElement, 'zefix-overridden');
            }
        }
    }
    private _zefixOverridden = false;
    /** Emitted once when the user edits a previously zefix-locked field after unlocking it. */
    @Output() zefixModified = new EventEmitter<void>();
    @Output() cancel = new EventEmitter<void>();

    // Instead of directly binding, we'll use a getter/setter.
    private _readonly = false;
    private _disabled = false;
    private _type;

    // Bind an attribute on the host element. For readonly, sometimes an empty string or null is used.
    @HostBinding('attr.readonly')
    get hostReadonlyAttr(): string | null {
        return this._readonly ? '' : null;
    }

    // Setter that updates both the native input element and its parent element.
    @Input()
    set readonly(value: boolean | string | null) {
        // Coerce the value if needed. For example, interpreting an empty string as true.
        // Here we use a simple check.
        this._readonly = value !== null && value !== false && value !== 'false';

        // Update the native input element's property.
        this.renderer.setProperty(this.el.nativeElement, 'readOnly', this._readonly);
    }
    get readonly(): boolean {
        return this._readonly;
    }

    // Setter that updates both the native input element and its parent element.
    @Input()
    set disabled(value: boolean | string | null) {
        // Coerce the value if needed. For example, interpreting an empty string as true.
        // Here we use a simple check.
        this._disabled = value !== null && value !== false && value !== 'false';

        // Update the native input element's property.
        this.renderer.setProperty(this.el.nativeElement, 'disabled', this._disabled);

        if (this.disabled) {
            if (this.numberLabelElement) {
                this.renderer.addClass(this.numberLabelElement, 'disabled');
            }
        } else {
            if (this.numberLabelElement) {
                this.renderer.removeClass(this.numberLabelElement, 'disabled');
            }
        }
    }
    get disabled(): boolean {
        return this._disabled;
    }

    // Bind an attribute on the host element. For type, sometimes an empty string or null is used.
    get hostTypeAttr(): string | null {
        return this._type ? '' : null;
    }

    @Input()
    set error(value: boolean | string | null) {
        const isError = value !== null && value !== false && value !== 'false';
        if (this.inputWrapperElement) {
            if (isError) {
                this.renderer.addClass(this.inputWrapperElement, 'error');
            } else {
                this.renderer.removeClass(this.inputWrapperElement, 'error');
            }
        }
    }

    // Setter that updates both the native input element and its parent element.
    @Input()
    set type(value: string | null) {
        this._type = value;

        // Update the native input element's property.
        this.renderer.setProperty(this.el.nativeElement, 'type', this._type);
    }
    get type(): string | null {
        return this._type;
    }

    ngAfterViewInit() {
        // Once the view is initialized, ensure the readonly state is applied.
        if (this._readonly && this.inputWrapperElement) {
            this.renderer.addClass(this.inputWrapperElement, 'readonly');
        }
        if (this._type && this.inputWrapperElement) {
            this.renderer.addClass(this.inputWrapperElement, 'type-' + this._type);
        }

        // Apply invalidRef styling
        this.applyInvalidRefStyling();

        // For number inputs with invalidRef, ensure label mode is shown initially
        if (this.invalidRef && this._type === 'number' && this.numberLabelElement && this.inputWrapperElement) {
            this.renderer.setStyle(this.numberLabelElement, 'display', 'block');
            this.renderer.setStyle(this.inputWrapperElement, 'display', 'none');
        }
    }

    private wrapperElement: HTMLElement;
    private labelElement: HTMLElement;
    private inputWrapperElement: HTMLElement;
    private anyIconWrapper: HTMLElement;
    private cancelIconWrapper: HTMLElement;
    private numberLabelElement: HTMLElement; // For number type, display as label when not focused
    private suffixIconWrapper: HTMLElement;
    private prefixIconWrapper: HTMLElement;
    private zefixPencilBtn: HTMLElement | null = null;
    private _zefixIsUnlocked = false;
    private listeners: (() => void)[] = [];
    private onChange: (_: any) => void = () => { };
    private onTouched: () => void = () => { };
    private currentValue: any; // Track the current form control value

    constructor(private el: ElementRef, private renderer: Renderer2, private finNumberPipe: AumFinancialNumberPipe) { }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['zefixLocked'] && this.inputWrapperElement) {
            if (this.zefixLocked) {
                this.applyZefixLocked();
            } else {
                this.removeZefixLocked();
            }
        }
        if (changes['zefixOverridden'] && this.inputWrapperElement) {
            if (this._zefixOverridden) {
                this.renderer.addClass(this.inputWrapperElement, 'zefix-overridden');
            } else {
                this.renderer.removeClass(this.inputWrapperElement, 'zefix-overridden');
            }
        }
        if (changes['aumicoStyleOptions'] && changes['aumicoStyleOptions'].currentValue != null) {
            this.applyStyles();
            this.updateNumberLabel();
        }
        if (changes['invalidRef']) {
            this.applyInvalidRefStyling();

            // Update the input value and display mode
            if (this.invalidRef) {
                // Set #REF! as value for all input types
                this.renderer.setProperty(this.el.nativeElement, 'value', '#REF!');

                // For number inputs, ensure label mode is shown
                if (this._type === 'number' && this.numberLabelElement && this.inputWrapperElement) {
                    this.renderer.setStyle(this.numberLabelElement, 'display', 'block');
                    this.renderer.setStyle(this.inputWrapperElement, 'display', 'none');
                }
            } else {
                // When invalidRef changes from true to false, restore the form control value
                // Get the current value from the form control (via the writeValue mechanism)
                if (this.currentValue !== undefined && this.currentValue !== null) {
                    this.renderer.setProperty(this.el.nativeElement, 'value', this.currentValue);
                } else {
                    // If no current value, clear the input
                    this.renderer.setProperty(this.el.nativeElement, 'value', '');
                }

                // For number inputs, update the label to reflect the restored value
                if (this._type === 'number' && this.numberLabelElement && this.inputWrapperElement) {
                    this.renderer.setStyle(this.numberLabelElement, 'display', 'block');
                    this.renderer.setStyle(this.inputWrapperElement, 'display', 'none');
                }
            }

            this.updateNumberLabel();
        }
    }

    ngOnInit() {
        // IMPORTANT: Since you are moving the element into a wrapper,
        // make sure to transfer the readonly attribute (if any) to the native input.
        if (this.readonly) {
            this.renderer.setProperty(this.el.nativeElement, 'readOnly', true);
        }

        // Create and insert the wrapper for the whole component
        this.wrapperElement = this.renderer.createElement('div');
        this.renderer.insertBefore(this.el.nativeElement.parentNode, this.wrapperElement, this.el.nativeElement);
        this.wrapperElement.classList.add('ham-input');

        // Create and insert the label
        if (this.label) {
            this.labelElement = this.renderer.createElement('label');
            const text = this.renderer.createText(this.label || this.el.nativeElement.placeholder);
            this.renderer.appendChild(this.labelElement, text);
            this.renderer.appendChild(this.wrapperElement, this.labelElement);

            if (this.showRequired) {
                this.renderer.addClass(this.labelElement, 'required');
            }
        }

        // Create and insert the wrapper for input and icon
        this.inputWrapperElement = this.renderer.createElement('div');
        this.renderer.appendChild(this.wrapperElement, this.inputWrapperElement);
        this.renderer.appendChild(this.inputWrapperElement, this.el.nativeElement);
        this.renderer.setStyle(this.inputWrapperElement, 'position', 'relative');
        this.renderer.addClass(this.inputWrapperElement, 'input-wrapper');

        if (this._type === 'number') {
            // Create a label element styled like the input
            this.numberLabelElement = this.renderer.createElement('div');
            this.renderer.addClass(this.numberLabelElement, 'input-number-label');
            if (this.disabled) {
                this.renderer.addClass(this.numberLabelElement, 'disabled');
            }
            const labelText = this.label || '';
            const textNode = this.renderer.createText(labelText);
            this.renderer.appendChild(this.numberLabelElement, textNode);
            // Insert the number label above the input wrapper element.
            this.renderer.insertBefore(this.wrapperElement, this.numberLabelElement, this.inputWrapperElement);
            // Make the number label focusable by keyboard (tab) and clickable.
            this.renderer.setAttribute(this.numberLabelElement, 'tabindex', '0');
            // Initially, show the label and hide the input field.
            this.renderer.setStyle(this.numberLabelElement, 'display', 'block');
            this.renderer.setStyle(this.inputWrapperElement, 'display', 'none');

            // When clicking or focusing the label, show the input field.
            this.listeners.push(
                this.renderer.listen(this.numberLabelElement, 'click', () => {
                    this.showInputField();
                }),
            );
            this.listeners.push(
                this.renderer.listen(this.numberLabelElement, 'focus', () => {
                    this.showInputField();
                }),
            );
        }

        if (this.icon) {
            this.anyIconWrapper = this.renderer.createElement('span');
            this.renderer.addClass(this.anyIconWrapper, 'ham-icon');
            this.renderer.addClass(this.anyIconWrapper, 'no-events');

            const iconElement = this.renderer.createElement('i');
            this.renderer.addClass(iconElement, 'any-icon');
            this.renderer.setStyle(iconElement, '-webkit-mask-image', `url(${this.icon})`);
            this.renderer.setStyle(iconElement, 'mask-image', `url(${this.icon})`);

            this.renderer.appendChild(this.anyIconWrapper, iconElement);

            this.renderer.appendChild(this.inputWrapperElement, this.anyIconWrapper);
            this.renderer.setStyle(this.anyIconWrapper, 'display', 'block');
            this.renderer.setStyle(this.anyIconWrapper, 'position', 'absolute');
            this.renderer.setStyle(this.anyIconWrapper, 'top', '0');
            this.renderer.setStyle(this.anyIconWrapper, 'left', '16px');
            this.renderer.setStyle(this.anyIconWrapper, 'transform', 'translate(0, 12.5px)');
            this.renderer.setStyle(this.anyIconWrapper, 'font-size', '16px');

            this.renderer.addClass(this.el.nativeElement, 'has-icon');
        }

        if (!this.hideRemoveXButton) {
            this.cancelIconWrapper = this.renderer.createElement('span');
            this.renderer.addClass(this.cancelIconWrapper, 'ham-icon');
            this.renderer.addClass(this.cancelIconWrapper, 'cancel-icon-wrapper');
            this.cancelIconWrapper.innerHTML = '<i class="x-icon"></i>';
            this.listeners.push(this.renderer.listen(this.cancelIconWrapper, 'click', () => this.onCancel()));
            this.renderer.appendChild(this.inputWrapperElement, this.cancelIconWrapper);
            this.renderer.setStyle(this.cancelIconWrapper, 'display', 'none');

            const toggleIconVisibilityListener = this.renderer.listen(this.el.nativeElement, 'input', () => {
                if (this.el.nativeElement.value && !this.suffixIcon && !this.zefixLocked) {
                    this.renderer.setStyle(this.cancelIconWrapper, 'display', 'block');
                } else {
                    this.renderer.setStyle(this.cancelIconWrapper, 'display', 'none');
                }
            });

            this.listeners.push(toggleIconVisibilityListener);

            setTimeout(() => {
                const computedStyle = window.getComputedStyle(this.el.nativeElement);
                if (computedStyle.textAlign === 'end') {
                    this.renderer.setStyle(this.cancelIconWrapper, 'right', '12px');
                }
            });
        }

        if (this.suffixIcon) {
            this.addSuffixIcon();
        }

        if (this.prefixIcon) {
            this.addPrefixIcon();
        }

        this.applyStyles();

        if (this.zefixLocked) {
            this.applyZefixLocked();
        }

        if (this._zefixOverridden) {
            this.renderer.addClass(this.inputWrapperElement, 'zefix-overridden');
        }
    }

    ngOnDestroy() {
        if (this.wrapperElement && this.wrapperElement.parentNode) {
            this.renderer.removeChild(this.wrapperElement.parentNode, this.wrapperElement);
        }

        this.listeners.forEach((listener) => listener());
    }

    writeValue(value: any): void {
        // Always track the current value from the form control
        this.currentValue = value;

        if (this.invalidRef) {
            // Set #REF! as the actual input value for both number and text inputs
            this.renderer.setProperty(this.el.nativeElement, 'value', '#REF!');
        } else {
            this.renderer.setProperty(this.el.nativeElement, 'value', value);
        }
        this.updateNumberLabel();
        this.updateHasValueClass();
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    private addSuffixIcon() {
        this.suffixIconWrapper = this.renderer.createElement('span');
        this.renderer.addClass(this.suffixIconWrapper, 'ham-icon');
        this.renderer.addClass(this.suffixIconWrapper, 'suffix-icon-wrapper');
        this.renderer.addClass(this.suffixIconWrapper, 'no-events');

        const iconElement = this.renderer.createElement('i');
        this.renderer.addClass(iconElement, 'suffix-icon');
        this.renderer.setStyle(iconElement, '-webkit-mask-image', `url(${this.suffixIcon})`);
        this.renderer.setStyle(iconElement, 'mask-image', `url(${this.suffixIcon})`);

        this.renderer.appendChild(this.suffixIconWrapper, iconElement);

        this.renderer.appendChild(this.inputWrapperElement, this.suffixIconWrapper);
        this.renderer.setStyle(this.suffixIconWrapper, 'display', 'block');
        this.renderer.setStyle(this.suffixIconWrapper, 'position', 'absolute');
        this.renderer.setStyle(this.suffixIconWrapper, 'top', '0');
        this.renderer.setStyle(this.suffixIconWrapper, 'right', '16px');
        this.renderer.setStyle(this.suffixIconWrapper, 'transform', 'translate(0, 15.5px)');
        this.renderer.setStyle(this.suffixIconWrapper, 'font-size', '14px');
    }

    private addPrefixIcon() {
        this.prefixIconWrapper = this.renderer.createElement('span');
        this.renderer.addClass(this.prefixIconWrapper, 'ham-icon');
        this.renderer.addClass(this.prefixIconWrapper, 'prefix-icon-wrapper');
        this.renderer.addClass(this.prefixIconWrapper, 'no-events');

        const iconElement = this.renderer.createElement('i');
        this.renderer.addClass(iconElement, 'prefix-icon');
        this.renderer.setStyle(iconElement, '-webkit-mask-image', `url(${this.prefixIcon})`);
        this.renderer.setStyle(iconElement, 'mask-image', `url(${this.prefixIcon})`);

        if (this.iconColor) {
            this.renderer.setStyle(iconElement, 'color', this.iconColor);
        }
        this.renderer.appendChild(this.prefixIconWrapper, iconElement);
        // Add to number label or input wrapper
        if (this.numberLabelElement) {
            this.renderer.setStyle(this.numberLabelElement, 'position', 'relative');
            // Add icon to number label

            this.renderer.insertBefore(
                this.numberLabelElement,
                this.prefixIconWrapper,
                this.numberLabelElement.firstChild,
            );
            this.renderer.setStyle(this.prefixIconWrapper, 'position', 'absolute');
            this.renderer.setStyle(this.prefixIconWrapper, 'left', '8px');
            this.renderer.setStyle(this.prefixIconWrapper, 'top', '50%');
            this.renderer.setStyle(this.prefixIconWrapper, 'transform', 'translateY(-50%)');
            this.renderer.setStyle(this.prefixIconWrapper, 'margin-right', '8px');
        }
    }

    @HostListener('input', ['$event.target'])
    onInput(target: HTMLInputElement) {
        if (this._zefixIsUnlocked) {
            this._zefixIsUnlocked = false;
            this.renderer.removeClass(this.inputWrapperElement, 'zefix-unlocked');
            this.renderer.addClass(this.inputWrapperElement, 'zefix-modified');
            this.zefixModified.emit();
        }

        const value = target.value;
        const processedValue = target.type === 'number' ? (value === '' ? null : parseFloat(value)) : value;

        this.onChange(processedValue);
        if (!this.hideRemoveXButton && !this.zefixLocked) {
            this.renderer.setStyle(this.cancelIconWrapper, 'display', value ? 'block' : 'none');
        }

        // Update the number label text to match the input's value.
        this.updateNumberLabel();
        this.updateHasValueClass();
    }

    @HostListener('focus')
    onFocus() {
        // Prevent focus on invalidRef inputs
        if (this.invalidRef) {
            this.el.nativeElement.blur();
            return;
        }

        if (this._type === 'number') {
            // If the input is read-only, do not show the input field.
            if (this.readonly) {
                this.renderer.setStyle(this.numberLabelElement, 'display', 'block');
                this.renderer.setStyle(this.inputWrapperElement, 'display', 'none');
            } else {
                // When not read-only, show the input field.
                this.showInputField();
            }
        } else {
            if (this.el.nativeElement.value) {
                if (!this.hideRemoveXButton && !this.zefixLocked) {
                    this.renderer.setStyle(this.cancelIconWrapper, 'display', 'block');
                }
            }
        }
    }

    @HostListener('blur')
    onBlur() {
        if (this._type === 'number') {
            // On blur, update the label and hide the input.
            this.updateNumberLabel();
            this.renderer.setStyle(this.numberLabelElement, 'display', 'block');
            this.renderer.setStyle(this.inputWrapperElement, 'display', 'none');
        }
        this.onTouched();
    }

    onCancel() {
        this.renderer.setProperty(this.el.nativeElement, 'value', '');
        if (!this.hideRemoveXButton) {
            this.renderer.setStyle(this.cancelIconWrapper, 'display', 'none');
        }
        this.el.nativeElement.dispatchEvent(new Event('input'));
        this.cancel.emit();
        this.updateHasValueClass();
    }

    private updateHasValueClass(): void {
        if (this.inputWrapperElement && this.shouldHighliteBlueWhenValueEntered) {
            const value = this.el.nativeElement.value;
            if (value && value.length > 0) {
                this.renderer.addClass(this.inputWrapperElement, 'has-value');
            } else {
                this.renderer.removeClass(this.inputWrapperElement, 'has-value');
            }
        }
    }

    private updateNumberLabel(): void {
        if (this._type === 'number' && this.numberLabelElement) {
            // Clear any existing content.
            this.numberLabelElement.textContent = '';

            // Handle invalidRef case first
            if (this.invalidRef) {
                const textNode = this.renderer.createText('#REF!');
                this.renderer.appendChild(this.numberLabelElement, textNode);
                this.renderer.setStyle(this.numberLabelElement, 'color', 'red');
                this.applyStyles();

                if (this.prefixIcon) {
                    this.addPrefixIcon();
                }
                return;
            }

            // Reset color for non-invalidRef cases
            this.renderer.removeStyle(this.numberLabelElement, 'color');

            // Retrieve the raw value.
            const rawValue = this.el.nativeElement.value;
            let textToDisplay: string;

            if (rawValue) {
                // Try to convert the string to a number.
                const numberValue = parseFloat(rawValue);
                if (!isNaN(numberValue)) {
                    // Format the value using your pipe.
                    let transformedNumberValue = numberValue;

                    if (this.aumicoStyleOptions?.includes(AumicoStyleOptions.FLIP_SIGN)) {
                        transformedNumberValue = NumberUtils.multiplyNumbers([transformedNumberValue, -1]);
                    }

                    // You can pass additional parameters here as needed
                    textToDisplay = this.finNumberPipe.transform(
                        transformedNumberValue,
                        `1.${this.decimalPlaces}-${this.decimalPlaces}`,
                        false,
                    );

                    if (this.aumicoStyleOptions?.includes(AumicoStyleOptions.PERCENTAGE)) {
                        textToDisplay = textToDisplay + ' %';
                    }
                } else {
                    // If the conversion fails, simply use the raw value.
                    textToDisplay = rawValue;
                }
            } else if (this.label) {
                textToDisplay = this.label;
            } else {
                // Use a non-breaking space if nothing is present.
                textToDisplay = '\u00A0';
            }

            const textNode = this.renderer.createText(textToDisplay);
            this.renderer.appendChild(this.numberLabelElement, textNode);
            this.applyStyles();

            if (this.prefixIcon) {
                this.addPrefixIcon();
            }
        }
    }

    private showInputField() {
        if (this._disabled) {
            return;
        }
        if (this._type === 'number') {
            if (this.readonly || this.invalidRef) {
                // For read-only or invalidRef, always display the label and hide the input.
                this.renderer.setStyle(this.numberLabelElement, 'display', 'block');
                this.renderer.setStyle(this.inputWrapperElement, 'display', 'none');
                return;
            }
            // Hide the number label and show the input field (for editable inputs).
            this.renderer.setStyle(this.numberLabelElement, 'display', 'none');
            this.renderer.setStyle(this.inputWrapperElement, 'display', 'block');
            // Delay the focus slightly to ensure the field is visible.
            setTimeout(() => {
                this.el.nativeElement.focus();
            });
        }
    }

    private applyZefixLocked(): void {
        this._zefixIsUnlocked = false;
        this.renderer.removeClass(this.inputWrapperElement, 'zefix-modified');
        this.renderer.addClass(this.inputWrapperElement, 'zefix-locked');
        this.renderer.setProperty(this.el.nativeElement, 'readOnly', true);

        if (this.cancelIconWrapper) {
            this.renderer.setStyle(this.cancelIconWrapper, 'display', 'none');
        }

        if (!this.zefixPencilBtn) {
            this.zefixPencilBtn = this.renderer.createElement('button');
            this.renderer.setAttribute(this.zefixPencilBtn, 'type', 'button');
            this.renderer.setAttribute(this.zefixPencilBtn, 'title', 'Bearbeiten');
            this.renderer.setAttribute(this.zefixPencilBtn, 'aria-label', 'Feld bearbeiten');
            this.renderer.addClass(this.zefixPencilBtn, 'zefix-pencil-btn');
            this.zefixPencilBtn.innerHTML =
                '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">' +
                '<path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>' +
                '<path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>' +
                '</svg>';
            this.listeners.push(
                this.renderer.listen(this.zefixPencilBtn, 'click', (e: Event) => {
                    e.stopPropagation();
                    this.unlockZefixField();
                }),
            );
            this.renderer.appendChild(this.inputWrapperElement, this.zefixPencilBtn);
        }
    }

    private unlockZefixField(): void {
        this._zefixIsUnlocked = true;
        this.renderer.removeClass(this.inputWrapperElement, 'zefix-locked');
        this.renderer.addClass(this.inputWrapperElement, 'zefix-unlocked');
        this.renderer.setProperty(this.el.nativeElement, 'readOnly', this._readonly);

        if (this.zefixPencilBtn) {
            this.renderer.removeChild(this.inputWrapperElement, this.zefixPencilBtn);
            this.zefixPencilBtn = null;
        }

        setTimeout(() => {
            this.el.nativeElement.focus();
            try {
                const len = this.el.nativeElement.value.length;
                this.el.nativeElement.setSelectionRange(len, len);
            } catch (_) {}
        }, 10);
    }

    private removeZefixLocked(): void {
        this.renderer.removeClass(this.inputWrapperElement, 'zefix-locked');
        this.renderer.removeClass(this.inputWrapperElement, 'zefix-unlocked');
        this.renderer.setProperty(this.el.nativeElement, 'readOnly', this._readonly);

        if (this.zefixPencilBtn) {
            this.renderer.removeChild(this.inputWrapperElement, this.zefixPencilBtn);
            this.zefixPencilBtn = null;
        }
    }

    private applyStyles(): void {
        if (this.aumicoStyleOptions == null) {
            return;
        }

        // Reset styles before applying new ones
        if (this.el.nativeElement) {
            this.renderer.setStyle(this.el.nativeElement, 'font-weight', 'normal');
            this.renderer.setStyle(this.el.nativeElement, 'text-align', 'left');
            this.renderer.setStyle(this.el.nativeElement, 'font-style', 'normal');
            this.renderer.setStyle(this.el.nativeElement, 'textDecoration', 'none');
        }

        if (this.numberLabelElement) {
            this.renderer.setStyle(this.numberLabelElement, 'font-weight', 'normal');
            this.renderer.setStyle(this.numberLabelElement, 'text-align', 'left');
            this.renderer.setStyle(this.numberLabelElement, 'font-style', 'normal');
            this.renderer.setStyle(this.numberLabelElement, 'textDecoration', 'none');
        }

        // Convert AumicoStyleOptions to an array to handle multiple styles
        const styles = Array.isArray(this.aumicoStyleOptions) ? this.aumicoStyleOptions : [this.aumicoStyleOptions];

        styles.forEach((style) => {
            switch (style) {
                case AumicoStyleOptions.BOLD:
                    if (this.el.nativeElement) {
                        this.renderer.setStyle(this.el.nativeElement, 'font-weight', 'bold');
                    }
                    if (this.numberLabelElement) {
                        this.renderer.setStyle(this.numberLabelElement, 'font-weight', 'bold');
                    }
                    break;
                case AumicoStyleOptions.ITALIC:
                    if (this.el.nativeElement) {
                        this.renderer.setStyle(this.el.nativeElement, 'font-style', 'italic');
                    }
                    if (this.numberLabelElement) {
                        this.renderer.setStyle(this.numberLabelElement, 'font-style', 'italic');
                    }
                    break;
                case AumicoStyleOptions.UNDERLINED:
                    if (this.el.nativeElement) {
                        this.renderer.setStyle(this.el.nativeElement, 'text-decoration', 'underline');
                    }
                    if (this.numberLabelElement) {
                        this.renderer.setStyle(this.numberLabelElement, 'text-decoration', 'underline');
                    }
                    break;
                case AumicoStyleOptions.LEFT_ALIGNED:
                    if (this.el.nativeElement) {
                        this.renderer.setStyle(this.el.nativeElement, 'text-align', 'left');
                    }
                    if (this.numberLabelElement) {
                        this.renderer.setStyle(this.numberLabelElement, 'text-align', 'left');
                    }
                    break;
                case AumicoStyleOptions.CENTER_ALIGNED:
                    if (this.el.nativeElement) {
                        this.renderer.setStyle(this.el.nativeElement, 'text-align', 'center');
                    }
                    if (this.numberLabelElement) {
                        this.renderer.setStyle(this.numberLabelElement, 'text-align', 'center');
                    }
                    break;
                case AumicoStyleOptions.RIGHT_ALIGNED:
                    if (this.el.nativeElement) {
                        this.renderer.setStyle(this.el.nativeElement, 'text-align', 'right');
                    }
                    if (this.numberLabelElement) {
                        this.renderer.setStyle(this.numberLabelElement, 'text-align', 'right');
                    }
                    break;
            }
        });
    }

    private applyInvalidRefStyling(): void {
        if (this.invalidRef) {
            // For non-number inputs, apply red color directly to the input
            if (this._type !== 'number') {
                this.renderer.setStyle(this.el.nativeElement, 'color', 'red');
                // Make the input readonly when invalidRef is true
                this.renderer.setProperty(this.el.nativeElement, 'readOnly', true);
            }

            // For number inputs, the styling is handled in updateNumberLabel
            // But we still need to make them readonly
            if (this._type === 'number') {
                this.renderer.setProperty(this.el.nativeElement, 'readOnly', true);
            }
        } else {
            // Reset styling when invalidRef is false
            if (this._type !== 'number') {
                this.renderer.removeStyle(this.el.nativeElement, 'color');
            } else if (this.numberLabelElement) {
                // For number inputs, reset the label color as well
                this.renderer.removeStyle(this.numberLabelElement, 'color');
            }

            // Restore original readonly state
            this.renderer.setProperty(this.el.nativeElement, 'readOnly', this._readonly);
        }
    }
}
